import InitialNode from 'component/organism/workflow/customNodes/initialNode/InitialNode';
import React, { useCallback, useEffect, useMemo } from 'react';
import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  BackgroundVariant,
  Controls,
  getConnectedEdges,
  getIncomers,
  getOutgoers,
  MarkerType,
  ReactFlow,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import MessageNode from 'component/organism/workflow/customNodes/message-node/MessageNode';
import QuestionNode from 'component/organism/workflow/customNodes/question-block/QuestionNode';
import REsponseAiNode from 'component/organism/workflow/customNodes/response-ai-node/REsponseAiNode';
import ImageNode from 'component/organism/workflow/customNodes/image-node/ImageNode';
import VideoNode from 'component/organism/workflow/customNodes/image-node/VideoNode';
import DocumentNode from 'component/organism/workflow/customNodes/document-node/DocumentNode';
import ConditionNode from 'component/organism/workflow/customNodes/condition-node/ConditionNode';
import EndNode from 'component/organism/workflow/customNodes/end-block/EndNode';
import FormNode from 'component/organism/workflow/customNodes/form-node/FormNode';
import CarouselNode from 'component/organism/workflow/customNodes/carousel-node/CarouselNode';
import CaptureNode from 'component/organism/workflow/customNodes/capture-node/CaptureNode';
import { useWorkflowContext } from 'app-context/worflow/WorkflowContext';
import toast from 'react-hot-toast';
import RedirectNode from 'component/organism/workflow/redirect-node/RedirectNode';
import { nodeType } from '../functions/nodeType';
import DataAiNode from 'component/organism/workflow/customNodes/data-ai-node/DataAiNode';
import SendMessageNode from 'component/organism/workflow/customNodes/send-message-node/SendMessageNode';
import SendEmailNode from 'component/organism/workflow/customNodes/send-email-node/SendEmailNode';
import ApiNode from 'component/organism/workflow/customNodes/api-node/ApiNode';
import SetVariablesNode from 'component/organism/workflow/customNodes/set-variables-node/SetVariablesNode';

const ReactFlowChart = ({ fetchedData, flag }) => {
  const {
    setSelectedNode,
    disableDrag,
    workflowData,
    setWorkflowData,
    type,
    setType,
  } = useWorkflowContext();
  const { nodes, edges } = workflowData;
  const { setViewport } = useReactFlow();
  const { screenToFlowPosition } = useReactFlow();
  useEffect(() => {
    if (fetchedData?.zoom !== undefined) {
      setViewport({
        zoom: fetchedData?.zoom,
        x: fetchedData?.viewPortX ?? 0,
        y: fetchedData?.viewPortY ?? 0,
      });
    }
  }, [fetchedData]);

  const onNodesChange = useCallback(
    (changes) =>
      setWorkflowData((prevData) => ({
        ...prevData,
        nodes: applyNodeChanges(changes, prevData.nodes),
      })),
    [edges],
  );

  const onEdgesChange = useCallback(
    (changes) =>
      setWorkflowData((prevData) => {
        const updatedEdges = applyEdgeChanges(changes, prevData.edges);
        return {
          ...prevData,
          edges: updatedEdges,
        };
      }),
    [nodes, edges],
  );

  const onConnect = useCallback((params) => {
    setWorkflowData((prev) => {
      const { source, target, sourceHandle } = params;
      const isSourceHandleConnected = prev.edges.some(
        (edge) => edge.sourceHandle === sourceHandle,
      );
      const isSourceConnected = prev.edges.some(
        (edge) => edge.source === source,
      );
      const isTargetConnected = prev.edges.some(
        (edge) => edge.target === target,
      );
      if (
        (sourceHandle == null && (isSourceConnected || isTargetConnected)) ||
        (sourceHandle !== null &&
          (isSourceHandleConnected || isTargetConnected))
      ) {
        toast.error('Each handle can only connect to one target');
        return prev;
      }
      const newEdge = {
        ...params,
        type: 'smoothstep',
        style: { strokeWidth: 1 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#7D7D7D' },
      };
      return {
        ...prev,
        edges: addEdge(newEdge, prev.edges),
      };
    });
  }, []);

  const onNodesDelete = useCallback(
    (deleted) => {
      setWorkflowData((prev) => ({
        ...prev,
        edges: deleted.reduce((acc, node) => {
          const incomers = getIncomers(node, nodes, edges);
          const outgoers = getOutgoers(node, nodes, edges);
          const connectedEdges = getConnectedEdges([node], edges);

          const remainingEdges = acc.filter(
            (edge) => !connectedEdges.includes(edge),
          );

          const createdEdges = incomers.flatMap(({ id: source }) =>
            outgoers?.map(({ id: target }) => ({
              id: `xy-edge__${source}_${target}`,
              source,
              target,
            })),
          );

          return [...remainingEdges, ...createdEdges];
        }, edges),
      }));
    },
    [edges, nodes, setWorkflowData],
  );

  const handleNodeClick = (e, node) => {
    setSelectedNode(node);
  };

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      if (!type) {
        return;
      }
      const newNodeId = (
        Math.max(
          0,
          ...(workflowData?.nodes?.map((node) => Number(node.id)) || []),
        ) + 1
      ).toString();

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      const newNode = {
        id: newNodeId,
        type: nodeType(type),
        nodeType: type,
        position,
        is_Error: true,
        data: { isNew: true },
      };
      setWorkflowData((prevParams) => ({
        ...prevParams,
        nodes: [...prevParams.nodes, newNode],
      }));
      setType(null);

      setTimeout(() => {
        setWorkflowData((prevParams) => ({
          ...prevParams,
          nodes: prevParams.nodes?.map((node) =>
            node.id === newNodeId
              ? { ...node, data: { ...node.data, isNew: false } }
              : node,
          ),
        }));
      }, 1000);
    },
    [screenToFlowPosition, type],
  );

  const onDragStart = (event, nodeType) => {
    setType(nodeType);
    event.dataTransfer.setData('text/plain', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  // useEffect(() => {
  //   const nodeIds = nodes.map((node) => node.id);
  //   const updatedEdges = edges.filter((edge) => nodeIds.includes(edge.target));
  //   if (updatedEdges.length !== edges.length) {
  //     setWorkflowData((prevData) => {
  //       return {
  //         ...prevData,
  //         edges: updatedEdges,
  //       };
  //     });
  //   }
  // }, [nodes, edges, setWorkflowData]);

  const nodeTypes = useMemo(
    () => ({
      initialNode: (props) => <InitialNode {...props} />,
      messageNode: (props) => <MessageNode {...props} />,
      questionNode: (props) => <QuestionNode {...props} />,
      responseAINode: (props) => <REsponseAiNode {...props} />,
      dataAINode: (props) => <DataAiNode {...props} />,
      imageNode: (props) => <ImageNode {...props} />,
      videoNode: (props) => <VideoNode {...props} />,
      documentNode: (props) => <DocumentNode {...props} />,
      conditionNode: (props) => <ConditionNode {...props} />,
      endNode: (props) => <EndNode {...props} />,
      formNode: (props) => <FormNode {...props} />,
      carouselNode: (props) => <CarouselNode {...props} />,
      captureNode: (props) => <CaptureNode {...props} />,
      redirectNode: (props) => <RedirectNode {...props} />,
      sendMessageNode: (props) => <SendMessageNode {...props} />,
      sendEmailNode: (props) => <SendEmailNode {...props} />,
      apiNode: (props) => <ApiNode {...props} />,
      setVariablesNode: (props) => <SetVariablesNode {...props} />,
    }),
    [],
  );

  useEffect(() => {
    const handleDeleteKeyPress = (event) => {
      const isTyping =
        document.activeElement &&
        (document.activeElement.tagName === 'INPUT' ||
          document.activeElement.tagName === 'TEXTAREA' ||
          document.activeElement.isContentEditable);

      if (!isTyping && (event.key === 'Delete' || event.key === 'Backspace')) {
        event.preventDefault();
        event.stopPropagation();
        setWorkflowData((prev) => {
          const selectedEdgeIds = prev.edges
            .filter((edge) => edge.selected)
            ?.map((edge) => edge.id);

          return {
            ...prev,
            edges: prev.edges.filter(
              (edge) => !selectedEdgeIds.includes(edge.id),
            ),
          };
        });
      }
    };

    document.addEventListener('keydown', handleDeleteKeyPress, true);
    return () => {
      document.removeEventListener('keydown', handleDeleteKeyPress, true);
    };
  }, [setWorkflowData]);

  // const edgeTypes = useMemo(
  //   () => ({
  //     customEdge: CustomEdge,
  //   }),
  //   []
  // );

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onConnect={onConnect}
      nodeTypes={nodeTypes}
      // edgeTypes={edgeTypes}
      onNodeClick={handleNodeClick}
      onNodesDelete={onNodesDelete}
      panOnDrag={disableDrag}
      zoomOnScroll={disableDrag}
      preventScrolling={disableDrag}
      zoomOnPinch={disableDrag}
      selectionOnDrag={disableDrag}
      nodesDraggable={disableDrag}
      nodesConnectable={disableDrag}
      elementsSelectable={disableDrag}
      // fitView
      deleteKeyCode={['Delete', 'Backspace']}
      onDrop={onDrop}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
    >
      <Controls />
      <Background
        // color="#F4F6F6"
        variant={BackgroundVariant.Dots}
        style={{ backgroundColor: '#F3F8FD' }}
      />
    </ReactFlow>
  );
};

export default ReactFlowChart;
